import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { getCategories } from '../../data/categoriesStore';
import styles from './ImportFacture.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParsedAttributes {
  [key: string]: string;
}

interface InvoiceLinePreview {
  lineIndex: number;
  supplierSku: string;
  originalDescription: string;
  parsedProductName: string;
  parsedVariantName: string;
  parsedAttributes: ParsedAttributes;
  quantity: number;
  purchasePriceHT: number;
  suggestedSalePriceHT: number;
  coefficient: number;
  matchedProductId: number | null;
  matchedProductName: string | null;
  matchedVariantId: number | null;
  matchedVariantSku: string | null;
  currentStock: number | null;
  currentPurchasePrice: number | null;
  actionType: string;
  actionLabel: string;
  detectedCategory: string | null;
  detectedSubCategory: string | null;
  matchedBySku: boolean;
  matchedProductCategory: string | null;
  matchedProductSubCat: string | null;
}

interface ImportSummary {
  totalLines: number;
  newProducts: number;
  newVariants: number;
  updatedVariants: number;
  totalPurchaseHT: number;
}

interface InvoicePreview {
  supplierName: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalHT: number;
  totalTTC: number;
  lines: InvoiceLinePreview[];
  summary: ImportSummary;
}

interface FournisseurResult {
  name: string;
  image: string;
  imageLarge: string;
  url: string;
  price: string;
  description: string;
}

interface ExistingProduct {
  id: number;
  name: string;
  category: string;
  price: number;
}

interface EditableLine extends InvoiceLinePreview {
  editedProductName: string;
  editedVariantName: string;
  editedSalePrice: number;
  editedCategory: string;
  editedSubCategory: string;
  editedSupplierSku: string;
  updatePurchasePrice: boolean;
  skip: boolean;

  // Correspondance auto — l'utilisateur peut la refuser
  dismissedMatch: boolean;

  // Association variante manuelle
  parentProductId: number | null;
  parentProductName: string | null;
  preCreatedProductId: number | null;
  variantPanelOpen: boolean;
  variantDbSearch: string;
  pendingType: 'none' | 'self' | 'db' | 'invoice';
  pendingProductId: number | null;
  pendingProductName: string;
  pendingVariantName: string;
  pendingInvoiceLineIdx: number | null;
  validating: boolean;

  // Correspondance BDD manuelle (bouton "Chercher dans la BDD")
  bddMatchProductId: number | null;
  bddMatchProductName: string | null;

  // Recherche fournisseur inline
  fournisseurOpen: boolean;
  fournisseurQuery: string;
  fournisseurResults: FournisseurResult[];
  fournisseurLoading: boolean;
  fournisseurError: string;
  fournisseurCreating: boolean;

  // Recherche BDD inline
  bddOpen: boolean;
  bddQuery: string;

  // Données enrichies depuis le fournisseur (image/description pour le confirm)
  pendingImageUrl: string;
  pendingImageExtension: string;
  pendingDescription: string;
}

interface PricePreset {
  id: string;
  category: string;
  subCategory: string;
  price: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v: number) => v.toFixed(2) + ' €';

const paDiffersSignificantly = (current: number, invoice: number) =>
  Math.abs(current - invoice) > 0.005;

function lineActionType(l: EditableLine): string {
  if (l.parentProductId !== null || l.preCreatedProductId !== null) return 'create_variant';
  if (l.matchedVariantId !== null) return 'update_variant';
  if (l.bddMatchProductId !== null) return 'update_stock';
  if (l.matchedBySku && l.matchedProductId !== null && !l.dismissedMatch) return 'update_stock';
  return 'create_product';
}

function lineMatchedProductId(l: EditableLine): number | null {
  if (l.parentProductId !== null) return l.parentProductId;
  if (l.preCreatedProductId !== null) return l.preCreatedProductId;
  if (l.matchedVariantId !== null) return l.matchedProductId;
  if (l.bddMatchProductId !== null) return l.bddMatchProductId;
  if (l.matchedBySku && l.matchedProductId !== null && !l.dismissedMatch) return l.matchedProductId;
  return null;
}

const toSlug = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// ─── PriceInput ───────────────────────────────────────────────────────────────
// Accepte virgule ou point comme séparateur décimal, parse uniquement à la fin

const PriceInput: React.FC<{ value: number; onChange: (v: number) => void; className?: string }> = ({ value, onChange, className }) => {
  const [text, setText] = React.useState(() => String(value).replace('.', ','));
  const prevValue = React.useRef(value);

  React.useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      setText(String(value).replace('.', ','));
    }
  }, [value]);

  return (
    <input
      type="text"
      value={text}
      className={className}
      onChange={e => {
        const raw = e.target.value;
        setText(raw);
        const val = parseFloat(raw.replace(',', '.'));
        if (!isNaN(val)) { prevValue.current = val; onChange(val); }
      }}
      onBlur={() => {
        const val = parseFloat(text.replace(',', '.'));
        const final = isNaN(val) ? 0 : val;
        prevValue.current = final;
        onChange(final);
        setText(String(final).replace('.', ','));
      }}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const ImportFacture: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [editableLines, setEditableLines] = useState<EditableLine[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [allProducts, setAllProducts] = useState<ExistingProduct[]>([]);
  const [debugText, setDebugText] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryList = useMemo(() => getCategories(), []);

  const [pricePresets, setPricePresets] = useState<PricePreset[]>(() => {
    try { return JSON.parse(localStorage.getItem('importFacture_pricePresets') ?? '[]'); } catch { return []; }
  });
  const [presetsOpen, setPresetsOpen] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('importFacture_pricePresets', JSON.stringify(pricePresets));
  }, [pricePresets]);

  // ── Presets de prix ───────────────────────────────────────────────────────

  const findPreset = useCallback((category: string, subCategory: string): number | null => {
    const exact = pricePresets.find(p => p.category === category && p.subCategory === (subCategory || ''));
    if (exact) return exact.price;
    const catOnly = pricePresets.find(p => p.category === category && p.subCategory === '');
    return catOnly?.price ?? null;
  }, [pricePresets]);

  const addPreset = () => setPricePresets(prev => [...prev, { id: Date.now().toString(), category: '', subCategory: '', price: 0 }]);
  const updatePreset = (id: string, updates: Partial<PricePreset>) => setPricePresets(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  const deletePreset = (id: string) => setPricePresets(prev => prev.filter(p => p.id !== id));

  // ── Mise à jour d'une ligne ────────────────────────────────────────────────

  const updateLine = useCallback((index: number, updates: Partial<EditableLine>) => {
    setEditableLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  // ── Upload & analyse ───────────────────────────────────────────────────────

  const handleDebugText = async () => {
    if (!file) return;
    setDebugLoading(true);
    setDebugText(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('invoices/debug-text', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDebugText(res.data.rawText);
    } catch (err: any) {
      setDebugText('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setDebugLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setPreview(null);
      setEditableLines([]);
      setImportResult(null);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) { setError('Veuillez sélectionner un fichier PDF'); return; }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.post('invoices/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        const data = response.data.data as InvoicePreview;
        setPreview(data);
        setEditableLines(data.lines.map(l => {
          // Si le code SKU est reconnu (variante ou supplier_sku) → on prend nom/catégorie depuis la BDD
          const skuMatched = l.matchedBySku || l.matchedVariantId !== null;
          return {
            ...l,
            editedProductName:  skuMatched ? (l.matchedProductName  || l.parsedProductName)          : l.parsedProductName,
            editedCategory:     skuMatched ? (l.matchedProductCategory || l.detectedCategory || '')   : (l.detectedCategory || ''),
            editedSubCategory:  skuMatched ? (l.matchedProductSubCat   || l.detectedSubCategory || '') : (l.detectedSubCategory || ''),
            editedVariantName:  l.parsedVariantName,
            editedSalePrice:    findPreset(l.detectedCategory || '', l.detectedSubCategory || '') ?? l.suggestedSalePriceHT,
            editedSupplierSku:  l.supplierSku,
            updatePurchasePrice: l.currentPurchasePrice === null,
            skip: false,
            dismissedMatch: false,
            parentProductId: null,
            parentProductName: null,
            preCreatedProductId: null,
            variantPanelOpen: false,
            variantDbSearch: '',
            pendingType: 'none' as const,
            pendingProductId: null,
            pendingProductName: l.parsedProductName,
            pendingVariantName: l.parsedVariantName,
            pendingInvoiceLineIdx: null,
            validating: false,
            bddMatchProductId:   null,
            bddMatchProductName: null,
            fournisseurOpen:    false,
            fournisseurQuery:   l.parsedProductName,
            fournisseurResults: [],
            fournisseurLoading: false,
            fournisseurError:   '',
            fournisseurCreating: false,
            bddOpen:  false,
            bddQuery: '',
            pendingImageUrl:       '',
            pendingImageExtension: '',
            pendingDescription:    '',
          };
        }));
        // Charger les produits existants en arrière-plan pour l'override de match
        api.get('products/admin').then(r => {
          const products: ExistingProduct[] = (r.data?.data?.products ?? []).map((p: any) => ({
            id: p.id,
            name: p.name,
            category: p.category,
            price: p.price ?? 0,
          }));
          setAllProducts(products);
        }).catch(() => {});
      } else {
        setError(response.data.message || "Erreur lors de l'analyse");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Erreur lors de l'upload");
    } finally {
      setLoading(false);
    }
  };

  // ── Recherche produits existants (panel variante) ────────────────────────

  const filteredProducts = useCallback((query: string) => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return allProducts.filter(p => p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [allProducts]);

  // ── Dissocier une variante ─────────────────────────────────────────────────

  const handleDisassociateVariant = (index: number) => {
    updateLine(index, {
      parentProductId: null,
      parentProductName: null,
      preCreatedProductId: null,
      variantPanelOpen: false,
    });
  };

  // ── Valider le panel variante ──────────────────────────────────────────────

  const handleVariantValider = async (index: number) => {
    const line = editableLines[index];
    if (line.pendingType === 'none') return;

    if (line.pendingType === 'db') {
      updateLine(index, {
        parentProductId: line.pendingProductId,
        parentProductName: line.pendingProductName,
        editedVariantName: line.pendingVariantName,
        variantPanelOpen: false,
      });
      return;
    }

    updateLine(index, { validating: true });
    const isInvoice = line.pendingType === 'invoice' && line.pendingInvoiceLineIdx !== null;
    const parentLine = isInvoice ? editableLines[line.pendingInvoiceLineIdx!] : line;

    try {
      const res = await api.post('products', {
        name: line.pendingProductName,
        slug: toSlug(line.pendingProductName),
        price: 0,
        category: parentLine.editedCategory || '',
        subCategory: parentLine.editedSubCategory || '',
        stock: 0,
        description: '',
        metaDescription: '',
        image: '',
        imageExtension: '',
        prixAchat: parentLine.purchasePriceHT,
        supplierSku: parentLine.editedSupplierSku || null,
        bestSeller: false,
        bonneAffaire: false,
        masquer: true,
      });
      const newId: number = res.data?.id ?? res.data?.data?.id;
      setAllProducts(prev => [...prev, { id: newId, name: line.pendingProductName, category: parentLine.editedCategory || '', price: 0 }]);

      if (isInvoice) {
        updateLine(line.pendingInvoiceLineIdx!, {
          preCreatedProductId: newId,
          editedVariantName: editableLines[line.pendingInvoiceLineIdx!].editedProductName,
        });
      }

      updateLine(index, {
        parentProductId: newId,
        parentProductName: line.pendingProductName,
        editedVariantName: line.pendingVariantName,
        variantPanelOpen: false,
        validating: false,
      });
    } catch (err: any) {
      updateLine(index, { validating: false });
      alert('Erreur création produit : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Confirmation ───────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!preview || editableLines.length === 0) return;
    setConfirming(true);
    setError('');
    try {
      const request = {
        supplierName: preview.supplierName,
        invoiceNumber: preview.invoiceNumber,
        invoiceDate: preview.invoiceDate,
        totalHT: preview.totalHT,
        totalTTC: preview.totalTTC,
        lines: editableLines
          .filter(l => !l.skip)
          .map(l => ({
            lineIndex: l.lineIndex,
            supplierSku: l.editedSupplierSku,
            productName: l.editedProductName,
            variantName: l.editedVariantName,
            attributes: l.parsedAttributes,
            quantity: l.quantity,
            purchasePriceHT: l.purchasePriceHT,
            salePriceHT: l.editedSalePrice,
            matchedProductId: lineMatchedProductId(l),
            matchedVariantId: l.matchedVariantId,
            actionType: lineActionType(l),
            category: l.editedCategory,
            subCategory: l.editedSubCategory || l.detectedSubCategory,
            updatePurchasePrice: l.updatePurchasePrice,
            supplierUrl: null,
            imageUrl: l.pendingImageUrl || null,
            imageExtension: l.pendingImageExtension || null,
            productDescription: l.pendingDescription || null,
          }))
      };
      const response = await api.post('invoices/confirm', request);
      if (response.data.success) {
        setImportResult(response.data.data);
        setPreview(null);
        setEditableLines([]);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setError(response.data.message || 'Erreur lors de la confirmation');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de la confirmation');
    } finally {
      setConfirming(false);
    }
  };

  // ── Recherche fournisseur par ligne ──────────────────────────────────────

  const handleFournisseurSearch = async (index: number) => {
    const line = editableLines[index];
    if (!line.fournisseurQuery.trim()) return;
    updateLine(index, { fournisseurLoading: true, fournisseurError: '', fournisseurResults: [] });
    try {
      const res = await api.get(`fournisseur/search?q=${encodeURIComponent(line.fournisseurQuery.trim())}`);
      const results: FournisseurResult[] = res.data.data ?? [];
      updateLine(index, { fournisseurLoading: false, fournisseurResults: results, fournisseurError: results.length === 0 ? 'Aucun résultat.' : '' });
    } catch (err: any) {
      updateLine(index, { fournisseurLoading: false, fournisseurError: err.response?.data?.message || err.message });
    }
  };

  const handleFournisseurSelect = async (index: number, p: FournisseurResult) => {
    const line = editableLines[index];
    updateLine(index, { fournisseurCreating: true, fournisseurError: '' });
    try {
      let description = p.description || '';
      if (p.url) {
        try {
          const det = await api.get(`fournisseur/detail?url=${encodeURIComponent(p.url)}`);
          if (det.data?.description) description = det.data.description;
        } catch { /* garder description de la liste */ }
      }
      const imgUrl = p.imageLarge || p.image || '';
      const ext = imgUrl ? (imgUrl.split('.').pop()?.toLowerCase().split('?')[0] ?? '') : '';
      const updates: Partial<EditableLine> = {
        pendingImageUrl: imgUrl,
        pendingImageExtension: ext,
        pendingDescription: description,
        fournisseurOpen: false,
        fournisseurResults: [],
        fournisseurCreating: false,
      };
      // Quand c'est une variante, on enrichit le produit parent — pas besoin de changer le nom de la ligne
      if (line.parentProductId === null) {
        updates.editedProductName = p.name;
      }
      updateLine(index, updates);
    } catch (err: any) {
      updateLine(index, { fournisseurCreating: false, fournisseurError: 'Erreur : ' + (err.response?.data?.message || err.message) });
    }
  };

  // ── Résumé calculé dynamiquement ──────────────────────────────────────────

  const liveSummary = useMemo(() => {
    const active = editableLines.filter(l => !l.skip);
    return {
      total: active.length,
      skipped: editableLines.length - active.length,
      newProducts: active.filter(l => lineActionType(l) === 'create_product').length,
      newVariants: 0,
      updates: active.filter(l => lineActionType(l) === 'update_variant' || lineActionType(l) === 'update_stock').length,
      totalPA: active.reduce((s, l) => s + l.purchasePriceHT * l.quantity, 0),
    };
  }, [editableLines]);

  // ── Rendu badge ───────────────────────────────────────────────────────────

  const renderBadge = (actionType: string, productName?: string | null) => {
    if (actionType === 'create_product') return <span className={`${styles.badge} ${styles.badgeNew}`}>Nouveau produit</span>;
    if (actionType === 'create_variant') return <span className={`${styles.badge} ${styles.badgeVariant}`}>Nouvelle variante</span>;
    if (actionType === 'update_variant') return <span className={`${styles.badge} ${styles.badgeUpdate}`}>Mise à jour stock</span>;
    if (actionType === 'update_stock')   return <span className={`${styles.badge} ${styles.badgeUpdate}`}>Mise à jour stock : {productName}</span>;
    return <span className={styles.badge}>{actionType}</span>;
  };

  // ── Rendu ligne ───────────────────────────────────────────────────────────

  const renderLine = (line: EditableLine, index: number) => {
    const act = lineActionType(line);
    const dbResults = filteredProducts(line.variantDbSearch);
    const selectableLines = editableLines.filter((l, i) =>
      i !== index && !l.skip && l.parentProductId === null && l.preCreatedProductId === null
    );
    const updateStockName =
      line.bddMatchProductId !== null ? line.bddMatchProductName :
      (line.matchedBySku && !line.dismissedMatch ? line.matchedProductName : null);
    const isUpdate = act === 'update_variant' || act === 'update_stock';
    const subCats = categoryList.find(c => c.name === line.editedCategory)?.subCategories ?? [];
    const paCurrentExists = line.currentPurchasePrice !== null;
    const paDiffers = paCurrentExists && paDiffersSignificantly(line.currentPurchasePrice!, line.purchasePriceHT);

    return (
      <div key={line.lineIndex} className={`${styles.lineCard} ${line.skip ? styles.lineSkipped : ''} ${styles[`line_${act}`] || ''}`}>
        {/* ── Header ── */}
        <div className={styles.lineHeader}>
          <div className={styles.lineHeaderLeft}>
            {renderBadge(act, updateStockName)}
            <span className={styles.lineOrigDesc}>{line.originalDescription}</span>
            <input className={styles.lineSku} value={line.editedSupplierSku}
              onChange={e => updateLine(index, { editedSupplierSku: e.target.value })}
              placeholder="Code produit" />
          </div>
          <div className={styles.lineHeaderRight}>
            <span className={styles.lineQty}>Qté&nbsp;<strong>{line.quantity}</strong></span>
            <span className={styles.linePA}>PA&nbsp;<strong>{fmt(line.purchasePriceHT)}</strong></span>
            <label className={styles.skipLabel}>
              <input type="checkbox" checked={line.skip} onChange={e => updateLine(index, { skip: e.target.checked })} />
              Ignorer
            </label>
          </div>
        </div>

        {!line.skip && (
          <div className={styles.lineBody}>

            {/* ── Correspondance auto backend — SKU reconnu ── */}
            {line.matchedBySku && line.matchedProductId !== null && line.matchedVariantId === null && !line.dismissedMatch && line.bddMatchProductId === null && (
              <div className={styles.autoMatchBanner} style={{ background: '#eafaf1', borderColor: '#27ae60', color: '#1e8449' }}>
                <span>🔑 Code produit reconnu → <strong>{line.matchedProductName}</strong><em className={styles.autoMatchSub}> (stock mis à jour)</em></span>
                <button onClick={() => updateLine(index, { dismissedMatch: true })} className={styles.btnDismissMatch}>✕ Ignorer</button>
              </div>
            )}

            {/* ── Correspondance auto backend — nom similaire ── */}
            {!line.matchedBySku && line.matchedProductId !== null && line.matchedVariantId === null && !line.dismissedMatch && line.bddMatchProductId === null && line.parentProductId === null && (
              <div className={styles.autoMatchBanner}>
                <span>🔍 Produit similaire → <strong>{line.matchedProductName}</strong></span>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    className={styles.btnDismissMatch}
                    style={{ background: '#27ae60', color: 'white', border: 'none' }}
                    onClick={() => updateLine(index, {
                      bddMatchProductId: line.matchedProductId,
                      bddMatchProductName: line.matchedProductName,
                      editedProductName: line.matchedProductName || line.editedProductName,
                      editedCategory: line.matchedProductCategory || line.editedCategory,
                      editedSubCategory: line.matchedProductSubCat || line.editedSubCategory,
                      updatePurchasePrice: true,
                      dismissedMatch: true,
                    })}
                  >✓ C'est ce produit</button>
                  <button onClick={() => updateLine(index, { dismissedMatch: true })} className={styles.btnDismissMatch}>✕ Non</button>
                </div>
              </div>
            )}

            {/* ── Bannière "variante de..." ── */}
            {(line.parentProductId !== null || line.preCreatedProductId !== null) && (
              <div className={styles.variantOfBanner}>
                <span>
                  {line.preCreatedProductId !== null
                    ? `Variante du produit "${line.editedProductName}" (pré-créé)`
                    : `Variante de "${line.parentProductName}"`}
                  {line.pendingImageUrl && <em style={{ marginLeft: '0.5rem', opacity: 0.8 }}>· 📷 image + description prêtes</em>}
                </span>
                <button onClick={() => handleDisassociateVariant(index)} className={styles.btnDisassociate}>✕ Dissocier</button>
              </div>
            )}

            {/* ── Bannière BDD match manuel ── */}
            {line.bddMatchProductId !== null && (
              <div className={styles.bddMatchBanner}>
                <span>📦 Mise à jour stock : <strong>{line.bddMatchProductName}</strong></span>
                <button
                  onClick={() => updateLine(index, { bddMatchProductId: null, bddMatchProductName: null })}
                  className={styles.btnDisassociate}
                >✕ Dissocier</button>
              </div>
            )}

            {/* ── Champs éditables ── */}
            <div className={styles.editRow}>
              <div className={styles.editField}>
                <label className={styles.editLabel}>Nom produit</label>
                <input type="text" value={line.editedProductName}
                  onChange={e => updateLine(index, { editedProductName: e.target.value })}
                  className={styles.editInput} />
              </div>
              <div className={styles.editField}>
                <label className={styles.editLabel}>Catégorie</label>
                <select value={line.editedCategory}
                  onChange={e => {
                    const cat = e.target.value;
                    const preset = findPreset(cat, '');
                    const upd: Partial<EditableLine> = { editedCategory: cat, editedSubCategory: '' };
                    if (preset !== null) upd.editedSalePrice = preset;
                    updateLine(index, upd);
                  }}
                  className={styles.editSelect}>
                  <option value="">— Choisir —</option>
                  {categoryList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              {subCats.length > 0 && (
                <div className={styles.editField}>
                  <label className={styles.editLabel}>Sous-catégorie</label>
                  <select value={line.editedSubCategory}
                    onChange={e => {
                      const sub = e.target.value;
                      const preset = findPreset(line.editedCategory, sub);
                      const upd: Partial<EditableLine> = { editedSubCategory: sub };
                      if (preset !== null) upd.editedSalePrice = preset;
                      updateLine(index, upd);
                    }}
                    className={styles.editSelect}>
                    <option value="">— Toutes —</option>
                    {subCats.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className={styles.editField}>
                <label className={styles.editLabel}>Prix vente (€)</label>
                <PriceInput
                  value={line.editedSalePrice}
                  onChange={v => updateLine(index, { editedSalePrice: v })}
                  className={`${styles.editInput} ${styles.priceInput}`}
                />
              </div>
            </div>

            {/* ── Infos stock & PA ── */}
            <div className={styles.infoRow}>
              <span className={styles.infoItem}>Stock actuel : <strong>{line.currentStock !== null ? line.currentStock : '—'}</strong></span>
              {paCurrentExists && (
                <span className={styles.infoItem}>
                  PA actuel : <strong>{fmt(line.currentPurchasePrice!)}</strong>
                  {paDiffers ? (
                    <span className={styles.paDiff}>
                      ⚠ Différent de {fmt(line.purchasePriceHT)}
                      <label className={styles.paUpdateLabel}>
                        <input type="checkbox" checked={line.updatePurchasePrice}
                          onChange={e => updateLine(index, { updatePurchasePrice: e.target.checked })} />
                        Mettre à jour
                      </label>
                    </span>
                  ) : <span className={styles.paOk}> ✓ identique</span>}
                </span>
              )}
              {!paCurrentExists && !isUpdate && (
                <span className={styles.infoItem}>
                  <label className={styles.paUpdateLabel}>
                    <input type="checkbox" checked={line.updatePurchasePrice}
                      onChange={e => updateLine(index, { updatePurchasePrice: e.target.checked })} />
                    Définir PA à {fmt(line.purchasePriceHT)}
                  </label>
                </span>
              )}
              <span className={styles.infoItem}>Coef. ×{line.coefficient.toFixed(2)}</span>
            </div>

            {/* ── Boutons actions ── */}
            {line.bddMatchProductId === null && (
              <div className={styles.variantBtnRow}>
                {line.parentProductId === null && line.preCreatedProductId === null && (
                  <button
                    className={`${styles.btnAddVariant} ${line.variantPanelOpen ? styles.btnAddVariantOpen : ''}`}
                    onClick={() => updateLine(index, {
                      variantPanelOpen: !line.variantPanelOpen,
                      fournisseurOpen: false,
                      bddOpen: false,
                      pendingType: 'none',
                      pendingProductName: line.editedProductName,
                      pendingVariantName: line.editedProductName,
                      pendingProductId: null,
                      pendingInvoiceLineIdx: null,
                      variantDbSearch: '',
                    })}
                  >
                    {line.variantPanelOpen ? '▲ Fermer' : '+ Ajouter comme variante de...'}
                  </button>
                )}
                <button
                  className={`${styles.btnFournisseur} ${line.fournisseurOpen ? styles.btnFournisseurOpen : ''}`}
                  onClick={() => updateLine(index, { fournisseurOpen: !line.fournisseurOpen, variantPanelOpen: false, bddOpen: false })}
                >
                  🛒 {line.fournisseurOpen ? '▲ Fermer' : (line.parentProductId !== null ? 'Enrichir le produit parent' : 'Chercher chez le fournisseur')}
                </button>
                <button
                  className={`${styles.btnBdd} ${line.bddOpen ? styles.btnBddOpen : ''}`}
                  onClick={() => updateLine(index, { bddOpen: !line.bddOpen, fournisseurOpen: false, variantPanelOpen: false, bddQuery: '' })}
                >
                  🔍 {line.bddOpen ? '▲ Fermer' : (line.parentProductId !== null ? 'Changer le produit parent' : 'Chercher dans la BDD')}
                </button>
              </div>
            )}

            {/* ── Panel recherche fournisseur ── */}
            {line.fournisseurOpen && (
              <div className={styles.fournisseurPanel}>
                <div className={styles.fournisseurSearchRow}>
                  <input
                    type="text"
                    value={line.fournisseurQuery}
                    onChange={e => updateLine(index, { fournisseurQuery: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleFournisseurSearch(index); }}
                    placeholder="Rechercher sur grossisteecigarette.fr..."
                    className={styles.fournisseurInput}
                    autoFocus
                  />
                  <button onClick={() => handleFournisseurSearch(index)} disabled={line.fournisseurLoading} className={styles.btnFournisseurSearch}>
                    {line.fournisseurLoading ? '...' : 'Rechercher'}
                  </button>
                </div>
                {line.fournisseurError && <div className={styles.fournisseurError}>{line.fournisseurError}</div>}
                {line.fournisseurCreating && <div className={styles.fournisseurCreating}>Chargement des données produit...</div>}
                {line.fournisseurResults.length > 0 && (
                  <div className={styles.fournisseurResults}>
                    {line.fournisseurResults.map((p, i) => (
                      <div key={i} className={styles.fournisseurItem} onClick={() => handleFournisseurSelect(index, p)}>
                        {p.image && <img src={p.image} alt="" className={styles.fournisseurImg} onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />}
                        <div className={styles.fournisseurItemInfo}>
                          <div className={styles.fournisseurItemName}>{p.name}</div>
                          {p.price && <div className={styles.fournisseurItemPrice}>{p.price}</div>}
                        </div>
                        <span className={styles.fournisseurItemImport}>⬇ Importer</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Panel recherche BDD ── */}
            {line.bddOpen && (
              <div className={styles.bddPanel}>
                <input
                  type="text"
                  value={line.bddQuery}
                  onChange={e => updateLine(index, { bddQuery: e.target.value })}
                  placeholder="Rechercher un produit existant..."
                  className={styles.bddSearchInput}
                  autoFocus
                />
                {(() => {
                  const q = line.bddQuery.toLowerCase().trim();
                  const results = q.length >= 1
                    ? allProducts.filter(p => p.name.toLowerCase().includes(q)).slice(0, 10)
                    : [];
                  if (!q) return <div className={styles.bddHint}>Tapez pour rechercher dans vos produits existants</div>;
                  if (results.length === 0) return <div className={styles.bddHint}>Aucun produit trouvé</div>;
                  return (
                    <div className={styles.bddResults}>
                      {results.map(p => (
                        <div key={p.id} className={styles.bddItem}
                          onClick={() => {
                            if (line.parentProductId !== null) {
                              // En mode variante : changer le produit parent
                              updateLine(index, {
                                parentProductId: p.id,
                                parentProductName: p.name,
                                bddOpen: false,
                                bddQuery: '',
                              });
                            } else {
                              updateLine(index, {
                                bddMatchProductId: p.id,
                                bddMatchProductName: p.name,
                                editedCategory: p.category || line.editedCategory,
                                editedSalePrice: p.price > 0 ? p.price : line.editedSalePrice,
                                bddOpen: false,
                                bddQuery: '',
                              });
                            }
                          }}
                        >
                          <span className={styles.bddItemName}>{p.name}</span>
                          {p.category && <span className={styles.bddItemCat}>{p.category}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* ── Panel variante ── */}
            {line.variantPanelOpen && (
              <div className={styles.variantPanel}>
                <div className={styles.variantSection}>
                  <div className={styles.variantSectionTitle}>Ce produit</div>
                  <div
                    className={`${styles.variantOption} ${line.pendingType === 'self' ? styles.variantOptionSelected : ''}`}
                    onClick={() => updateLine(index, { pendingType: 'self', pendingProductName: line.editedProductName, pendingVariantName: line.editedProductName, pendingProductId: null, pendingInvoiceLineIdx: null })}
                  >
                    <span className={styles.variantOptionName}>↩ Même produit</span>
                    <span className={styles.variantOptionCat}>Crée ce produit et l'associe comme sa propre variante</span>
                  </div>
                </div>

                {selectableLines.length > 0 && (
                  <div className={styles.variantSection}>
                    <div className={styles.variantSectionTitle}>Dans cette facture</div>
                    {selectableLines.map(otherLine => {
                      const otherIdx = editableLines.indexOf(otherLine);
                      const selected = line.pendingType === 'invoice' && line.pendingInvoiceLineIdx === otherIdx;
                      return (
                        <div key={otherLine.lineIndex}
                          className={`${styles.variantOption} ${selected ? styles.variantOptionSelected : ''}`}
                          onClick={() => updateLine(index, { pendingType: 'invoice', pendingInvoiceLineIdx: otherIdx, pendingProductName: otherLine.editedProductName, pendingVariantName: line.editedProductName, pendingProductId: null })}
                        >
                          <span className={styles.variantOptionName}>{otherLine.editedProductName}</span>
                          <span className={styles.variantOptionCat}>{otherLine.editedSupplierSku}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className={styles.variantSection}>
                  <div className={styles.variantSectionTitle}>Produit existant en base</div>
                  <input type="text" placeholder="Rechercher un produit..."
                    value={line.variantDbSearch}
                    onChange={e => updateLine(index, { variantDbSearch: e.target.value })}
                    className={styles.variantDbSearchInput} />
                  {dbResults.length > 0 && (
                    <div className={styles.variantDbResults}>
                      {dbResults.map(p => (
                        <div key={p.id}
                          className={`${styles.variantOption} ${line.pendingType === 'db' && line.pendingProductId === p.id ? styles.variantOptionSelected : ''}`}
                          onClick={() => updateLine(index, { pendingType: 'db', pendingProductId: p.id, pendingProductName: p.name, pendingVariantName: line.editedProductName, pendingInvoiceLineIdx: null })}
                        >
                          <span className={styles.variantOptionName}>{p.name}</span>
                          <span className={styles.variantOptionCat}>{p.category}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {line.pendingType !== 'none' && (
                  <div className={styles.variantConfirmFields}>
                    <div className={styles.variantFieldGroup}>
                      <label className={styles.editLabel}>Nom produit parent</label>
                      <input type="text" value={line.pendingProductName}
                        onChange={e => updateLine(index, { pendingProductName: e.target.value })}
                        className={styles.editInput} />
                    </div>
                    <div className={styles.variantFieldGroup}>
                      <label className={styles.editLabel}>Nom de cette variante</label>
                      <input type="text" value={line.pendingVariantName}
                        onChange={e => updateLine(index, { pendingVariantName: e.target.value })}
                        className={styles.editInputSmall} />
                    </div>
                  </div>
                )}

                <div className={styles.variantPanelFooter}>
                  <button onClick={() => updateLine(index, { variantPanelOpen: false })} className={styles.btnVariantCancel}>Annuler</button>
                  <button onClick={() => handleVariantValider(index)} disabled={line.pendingType === 'none' || line.validating} className={styles.btnVariantValider}>
                    {line.validating ? 'Création...' : '✓ Valider'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Rendu principal ───────────────────────────────────────────────────────

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Import Facture Fournisseur</h1>

        {/* Zone upload */}
        <div className={styles.uploadBox}>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            id="pdf-upload"
            className={styles.fileInput}
          />
          <label htmlFor="pdf-upload" className={styles.fileLabel}>
            {file ? `📄 ${file.name}` : 'Choisir un fichier PDF'}
          </label>
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className={styles.btnUpload}
          >
            {loading ? 'Analyse en cours...' : 'Analyser la facture'}
          </button>
        </div>

        {/* Bouton debug texte brut */}
        {file && (
          <div style={{ marginBottom: '1rem' }}>
            <button onClick={handleDebugText} disabled={debugLoading} className={styles.btnUpload} style={{ background: '#888', fontSize: '0.82rem' }}>
              {debugLoading ? 'Extraction...' : '🔍 Debug: voir texte brut PDF'}
            </button>
            {debugText && (
              <pre style={{ background: '#1a1a2e', color: '#aef', padding: '1rem', borderRadius: '8px', fontSize: '0.75rem', maxHeight: '400px', overflow: 'auto', marginTop: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {debugText}
              </pre>
            )}
          </div>
        )}

        {error && <div className={styles.errorBox}>{error}</div>}

        {/* Résultat d'import */}
        {importResult && (
          <div className={styles.successBox}>
            <h2>✅ Import réussi</h2>
            <p>{importResult.message}</p>
            <div className={styles.resultStats}>
              <span>Produits créés : <strong>{importResult.productsCreated}</strong></span>
              <span>Variantes créées : <strong>{importResult.variantsCreated}</strong></span>
              <span>Variantes mises à jour : <strong>{importResult.variantsUpdated}</strong></span>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className={styles.previewSection}>
            {/* En-tête facture */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceTitle}>
                <h2>Facture #{preview.invoiceNumber}</h2>
                <span className={styles.invoiceDate}>
                  {new Date(preview.invoiceDate).toLocaleDateString('fr-FR')}
                </span>
              </div>
              <div className={styles.invoiceGrid}>
                <div><span className={styles.invoiceFieldLabel}>Fournisseur</span>{preview.supplierName}</div>
                <div><span className={styles.invoiceFieldLabel}>Total HT</span>{fmt(preview.totalHT)}</div>
                <div><span className={styles.invoiceFieldLabel}>Total TTC</span>{fmt(preview.totalTTC)}</div>
              </div>
            </div>

            {/* Résumé dynamique */}
            <div className={styles.summaryBar}>
              <div className={styles.summaryChip}>
                <strong>{liveSummary.total}</strong> lignes actives
              </div>
              <div className={`${styles.summaryChip} ${styles.chipNew}`}>
                <strong>{liveSummary.newProducts}</strong> nouveaux produits
              </div>
              <div className={`${styles.summaryChip} ${styles.chipVariant}`}>
                <strong>{liveSummary.newVariants}</strong> nouvelles variantes
              </div>
              <div className={`${styles.summaryChip} ${styles.chipUpdate}`}>
                <strong>{liveSummary.updates}</strong> mises à jour stock
              </div>
              {liveSummary.skipped > 0 && (
                <div className={`${styles.summaryChip} ${styles.chipSkip}`}>
                  <strong>{liveSummary.skipped}</strong> ignorées
                </div>
              )}
              <div className={`${styles.summaryChip} ${styles.chipTotal}`}>
                Total PA : <strong>{fmt(liveSummary.totalPA)}</strong>
              </div>
            </div>

            {/* Prix par défaut */}
            <div className={styles.presetsSection}>
              <button onClick={() => setPresetsOpen(o => !o)} className={styles.btnPresetsToggle}>
                💰 Prix de vente par défaut {presetsOpen ? '▲' : '▼'}
                {pricePresets.length > 0 && <span className={styles.presetsBadge}>{pricePresets.length}</span>}
              </button>
              {presetsOpen && (
                <div className={styles.presetsBody}>
                  {pricePresets.length === 0 && (
                    <p className={styles.presetsEmpty}>Aucun preset. Ajoute une règle ci-dessous.</p>
                  )}
                  {pricePresets.map(preset => {
                    const presetSubCats = categoryList.find(c => c.name === preset.category)?.subCategories ?? [];
                    return (
                      <div key={preset.id} className={styles.presetRow}>
                        <select value={preset.category}
                          onChange={e => updatePreset(preset.id, { category: e.target.value, subCategory: '' })}
                          className={styles.editSelect}>
                          <option value="">— Catégorie —</option>
                          {categoryList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                        {presetSubCats.length > 0 ? (
                          <select value={preset.subCategory}
                            onChange={e => updatePreset(preset.id, { subCategory: e.target.value })}
                            className={styles.editSelect}>
                            <option value="">— Toutes sous-cat. —</option>
                            {presetSubCats.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : <span className={styles.presetsNoSub}>—</span>}
                        <div className={styles.presetPriceWrap}>
                          <PriceInput
                            value={preset.price}
                            onChange={v => updatePreset(preset.id, { price: v })}
                            className={styles.presetPriceInput}
                          />
                          <span className={styles.presetEuro}>€</span>
                        </div>
                        <button onClick={() => deletePreset(preset.id)} className={styles.btnDeletePreset}>✕</button>
                      </div>
                    );
                  })}
                  <button onClick={addPreset} className={styles.btnAddPreset}>+ Ajouter une règle</button>
                </div>
              )}
            </div>

            {/* Sélection globale */}
            <div className={styles.bulkBar}>
              <button
                className={styles.btnBulkSkip}
                onClick={() => setEditableLines(prev => prev.map(l => ({ ...l, skip: true })))}
              >
                ☐ Ignorer tout
              </button>
              <button
                className={styles.btnBulkKeep}
                onClick={() => setEditableLines(prev => prev.map(l => ({ ...l, skip: false })))}
              >
                ☑ Garder tout
              </button>
              {editableLines.some(l => !l.matchedBySku && l.matchedProductId !== null && !l.dismissedMatch && l.bddMatchProductId === null && l.parentProductId === null) && (
                <button
                  className={styles.btnBulkAttribuer}
                  onClick={() => setEditableLines(prev => prev.map(l => {
                    if (!l.matchedBySku && l.matchedProductId !== null && !l.dismissedMatch && l.bddMatchProductId === null && l.parentProductId === null) {
                      return {
                        ...l,
                        bddMatchProductId: l.matchedProductId,
                        bddMatchProductName: l.matchedProductName,
                        editedProductName: l.matchedProductName || l.editedProductName,
                        editedCategory: l.matchedProductCategory || l.editedCategory,
                        editedSubCategory: l.matchedProductSubCat || l.editedSubCategory,
                        updatePurchasePrice: true,
                        dismissedMatch: true,
                      };
                    }
                    return l;
                  }))}
                >
                  ✓ Attribuer tous les similaires
                </button>
              )}
            </div>

            {/* Liste des lignes */}
            <div className={styles.linesList}>
              {editableLines.map((line, idx) => renderLine(line, idx))}
            </div>

            {/* Actions */}
            <div className={styles.actionsBar}>
              <button
                onClick={() => {
                  setPreview(null);
                  setEditableLines([]);
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className={styles.btnCancel}
              >
                Annuler
              </button>
              <div className={styles.actionsRight}>
                <span className={styles.confirmSummary}>
                  {liveSummary.total} lignes à importer · Total PA {fmt(liveSummary.totalPA)}
                </span>
                <button
                  onClick={handleConfirm}
                  disabled={confirming || liveSummary.total === 0}
                  className={styles.btnConfirm}
                >
                  {confirming ? 'Import en cours...' : `✅ Valider l'import (${liveSummary.total} lignes)`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
